/**
 * Reserved root-namespace names.
 *
 * The Putty viewer (`putty.me`) uses a single global root namespace shared by
 * usernames and group slugs (`putty.me/{name}`). These names are reserved for
 * URL prefixes, app routes, and platform infrastructure — they cannot be
 * claimed as a username or as a layer-0 (group) collection slug.
 *
 * Includes:
 *   - URL prefixes used by the viewer (`b`, `g`, `i`)
 *   - Reserved root-level app routes (`dev`, `components`, `exp`, `auth`, etc.)
 *   - Platform / common-sense terms (`api`, `admin`, `settings`, `me`, etc.)
 *   - Single-letter and very common short tokens we want to keep available
 *     for future infra use (`c`, `u`, `x`)
 *
 * Comparisons are case-insensitive — names should be normalized to lowercase
 * before checking.
 */
const RESERVED_ROOT_NAME_LIST = [
    // URL primitive prefixes
    "b", // putty.me/b/{slug} — block deeplinks
    "g", // putty.me/g/{shortId} — member-only group/collection route
    "i", // putty.me/i/{token} — capability-token invites (follow-on)
    "c", // future capability prefix
    "u", // future user-section prefix
    // App routes that already exist or are imminent in the viewer
    "dev",
    "components",
    "exp",
    "auth",
    "login",
    "logout",
    "signup",
    "settings",
    "preferences",
    "me",
    "home",
    "search",
    "help",
    "support",
    "about",
    "legal",
    "terms",
    "privacy",
    // Platform / infra
    "api",
    "app",
    "admin",
    "static",
    "assets",
    "public",
    "internal",
    "health",
    "status",
    "webhook",
    "webhooks",
    "cdn",
    "media",
    "files",
    "uploads",
    // Common reserved terms across the web
    "www",
    "mail",
    "email",
    "blog",
    "docs",
    "doc",
    "store",
    "shop",
    "x", // very short, ambiguous
    // Putty-specific concepts that should stay available for product use
    // "putty",
    "viewer",
    "explorer",
    "stream",
    "collection",
    "collections",
    "group",
    "groups",
    "block",
    "blocks",
    "memory",
    "memories",
    "folder",
    "folders",
    "tag",
    "tags",
    "taxonomy",
    "taxonomies",
];
/**
 * Read-only set of reserved root-namespace names. All entries are lowercase.
 */
export const RESERVED_ROOT_NAMES = new Set(RESERVED_ROOT_NAME_LIST);
/**
 * Returns true if a candidate name is reserved (case-insensitive).
 *
 * @example
 * isReservedRootName("dev")       // true
 * isReservedRootName("DEV")       // true (case-insensitive)
 * isReservedRootName("alex")      // false
 */
export function isReservedRootName(name) {
    return RESERVED_ROOT_NAMES.has(name.toLowerCase());
}
